import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'mcp-trigger',
  version: '1.0.0',
  properties: [
    {
      key: 'mcpServerUrl',
      label: 'MCP Server URL',
      type: 'notice',
      default: '',
      section: 'deployment',
      description: 'The POST endpoint for calling this workflow as an MCP tool',
    },
    {
      key: 'mcpTestBody',
      label: 'Test Request Body',
      type: 'notice',
      default: '',
      section: 'deployment',
      description: 'JSON-RPC 2.0 request body for testing in Postman or curl',
    },
    {
      key: 'toolName',
      label: 'Tool Name',
      type: 'string',
      required: true,
      placeholder: 'my_workflow_tool',
      description: 'The name of the tool as it will appear to MCP clients',
      typeOptions: {
        rows: 1,
      },
      validation: [
        {
          type: 'pattern',
          pattern: '^[a-zA-Z0-9_-]+$',
          message: 'Tool name must contain only alphanumeric characters, hyphens, and underscores',
        },
      ],
    },
    {
      key: 'toolDescription',
      label: 'Tool Description',
      type: 'string',
      required: true,
      placeholder: 'Describe what this tool does...',
      description: 'A clear description of what this tool does (helps AI agents understand when to use it)',
      typeOptions: {
        rows: 3,
      },
    },
    {
      key: 'inputSchema',
      label: 'Input Schema',
      type: 'json',
      required: false,
      placeholder: '{\n  "type": "object",\n  "properties": {\n    "query": {\n      "type": "string",\n      "description": "Search query"\n    }\n  },\n  "required": ["query"]\n}',
      description: 'JSON Schema defining the input parameters for this tool',
    },
    {
      key: 'transportType',
      label: 'Transport Type',
      type: 'options',
      required: true,
      default: 'sse',
      options: [
        { value: 'sse', name: 'Server-Sent Events (SSE)' },
        { value: 'http', name: 'HTTP Streamable' },
      ],
      description: 'MCP transport protocol to use',
    },
    {
      key: 'authentication',
      label: 'Authentication',
      type: 'options',
      default: 'none',
      options: [
        { value: 'none', name: 'None (Public)' },
        { value: 'bearer', name: 'Bearer Token' },
        { value: 'header', name: 'Custom Header' },
      ],
      description: 'Authentication method for incoming requests',
    },
    {
      key: 'bearerToken',
      label: 'Bearer Token',
      type: 'string',
      required: false,
      placeholder: 'your-secret-token',
      description: 'Bearer token that clients must provide',
      displayOptions: {
        show: {
          authentication: ['bearer'],
        },
      },
    },
    {
      key: 'customHeaderName',
      label: 'Header Name',
      type: 'string',
      required: false,
      placeholder: 'X-API-Key',
      description: 'Custom header name for authentication',
      displayOptions: {
        show: {
          authentication: ['header'],
        },
      },
    },
    {
      key: 'customHeaderValue',
      label: 'Header Value',
      type: 'string',
      required: false,
      placeholder: 'your-api-key',
      description: 'Expected value for the custom header',
      displayOptions: {
        show: {
          authentication: ['header'],
        },
      },
    },
    {
      key: 'responseMode',
      label: 'Response Mode',
      type: 'options',
      default: 'wait',
      options: [
        { value: 'wait', name: 'Wait for Workflow Completion' },
        { value: 'immediate', name: 'Immediate Acknowledgment' },
        { value: 'respond-node', name: 'Use Respond to Trigger Node' },
      ],
      description: 'Whether to wait for workflow completion before responding',
    },
    {
      key: 'timeout',
      label: 'Timeout (seconds)',
      type: 'number',
      default: 300,
      description: 'Maximum time to wait for workflow completion (in seconds)',
      displayOptions: {
        show: {
          responseMode: ['wait'],
        },
      },
    },
    {
      key: 'enableCors',
      label: 'Enable CORS',
      type: 'boolean',
      default: true,
      description: 'Allow cross-origin requests',
    },
    {
      key: 'testInput',
      label: 'Test Input',
      type: 'json',
      required: false,
      placeholder: '{\n  "query": "test search",\n  "limit": 10\n}',
      description: 'Test input data to use when testing this workflow in the editor (not used for actual MCP calls). Should match your tool\'s input schema.',
      default: '{\n  "query": "test search",\n  "limit": 10\n}',
      customComponent: 'MCPTestMessageField',
    },
  ],
};

export default schema;
